export type Level = 'A2' | 'B1' | 'B2' | 'C1';
export type Pace = 'relaxed' | 'regular' | 'intense';
export type Lang = 'RU' | 'EN';
/** Роль аккаунта. GUEST по умолчанию у всех; ADMIN выставляется вручную в БД и
    открывает админ-вкладку. Должна совпадать с backend ProfileRole. */
export type Role = 'GUEST' | 'ADMIN';

/** Языки, поддерживаемые модулями (ISO 639-1). source_lang — что юзер изучает,
    target_lang — на каком языке видит переводы. Должен совпадать с backend
    enum Language. Расширять одновременно с бэком. */
export type Language = 'en' | 'ru' | 'de' | 'fr' | 'es' | 'it';

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: 'English',
  ru: 'Русский',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
};

export const LANGUAGE_FLAGS: Record<Language, string> = {
  en: '🇬🇧',
  ru: '🇷🇺',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  it: '🇮🇹',
};
export type ModuleState = 'new' | 'progress' | 'due' | 'done';
export type WordState = 'new' | 'review' | 'learned';
export type AuthStage = 'auth' | 'onboarding' | 'app';

export interface User {
  id: string;
  name: string;
  email: string;
  initials: string;
  emailVerified: boolean;
  /** Почта не подтверждена, а бесплатная своя карточка ещё не потрачена (профиль `guest_card_available`, 2026-11-02):
   *  строка создания открыта для одного слова. С подтверждённой почтой — false. */
  guestCardAvailable?: boolean;
  level?: Level;
  dailyGoal?: number;
  pace?: Pace;
  notifications?: boolean;
  sounds?: boolean;
  lang?: Lang;
  newPerSession?: number;
  /** Автопереход к следующей карточке после верного ответа (profile.auto_advance).
   *  По умолчанию false. Сессия читает флаг из стора, чтобы гейтить авто-таймер. */
  autoAdvance?: boolean;
  /** Роль аккаунта. Приходит из GET /profile. Управляет видимостью админ-вкладки
   *  на фронте; доступ к /api/v1/admin/* всё равно проверяется на бэке. */
  role?: Role;
  /** Поддерживал ли проект донатом. Из GET /profile (user.has_donated). Включает
   *  плашку «Поддержал проект» на главной. */
  hasDonated?: boolean;
}

export interface ModuleSummary {
  id: string;
  title: string;
  desc: string;
  words: number;
  due: number;
  fresh: number;
  progress: number;
  state: ModuleState;
  active: boolean;
  deleted: boolean;
  isPublic: boolean;
  preview: string[];
  cards: Word[];
  /** Язык, который изучает юзер в этом модуле (front_name карточек). */
  sourceLang: Language;
  /** Язык переводов в этом модуле. */
  targetLang: Language;
}

export interface WordTranslation {
  id: string;
  text: string;
  gender: string | null;
}

export interface WordSense {
  id: string;
  translations: WordTranslation[];
  enSynonyms: string[];
  exampleEn: string | null;
  /** Очень простое предложение со словом в этом смысле — по нему видно, какой это
   *  перевод (ознакомление). null у старых и ручных карточек. */
  simpleExampleEn?: string | null;
  /** Перевод простого предложения (2026-10-07): пара «предложение — перевод» в очереди
   *  создания. null — перевода нет (старые и ручные карточки). */
  simpleExampleRu?: string | null;
  hint: string | null;
  /** Развёрнутый смысл на target_lang (что/в каком контексте значит). Опционально:
   *  заполняется там, где карточку показываем детально (вне сессии). */
  definition?: string | null;
  selectionOptions: string[] | null;
  /** «Редкие» (2026-09-23): переводы строки словаря вне смыслов карточки — без
   *  примера и подсказки. Показываются строкой «редкие: …», вопросом не бывают. */
  isRare?: boolean;
  /** Другие употребления слова с тем же переводом (2026-09-25): основной контекст —
   *  exampleEn/hint/definition самого смысла, эти тренировка чередует между показами. */
  contexts?: SenseContext[];
}

export interface SenseContext {
  exampleEn: string | null;
  simpleExampleEn: string | null;
  hint: string | null;
  definition: string | null;
}

export interface WordMeaning {
  id: string;
  pos: string | null;
  senses: WordSense[];
}

export interface Word {
  id?: string;
  /** id конкретного user_card_state (per-pos, уникален в отличие от id=card_id).
   *  Нужен для выбора карточек в «Тренировке»: у слова с несколькими значениями
   *  один card_id, но разные state — выбираем по отдельности. */
  stateId?: string;
  /** id части речи (meaning) этого state. Нужен для «скрыть/восстановить из модуля»
   *  (POST /cards/{id}/hide?meaning_id=). undefined у pending-карточек (нет real-state). */
  meaningId?: string;
  word: string;
  ipa: string;
  /** Первичный перевод для превью — первый перевод первого sense. Пустая строка до генерации. */
  ru: string;
  state: WordState;
  isNeedAction?: boolean;
  /** Скрыта из модуля (кнопка-мусорка). Показывается только во вкладке «Скрытые»,
   *  не участвует в учёбе/счётчиках. Восстанавливается из той же вкладки. */
  isHidden?: boolean;
  /** Карточка заведена вручную (status === 'manual'), не сгенерирована AI. */
  isManual?: boolean;
  /** Фразовый глагол (go out): подпись «phrasal verb» вместо verb. */
  isPhrasalVerb?: boolean;
  errorInfo?: string | null;
  expectWord?: string[] | null;
  /** Расширенная словарная структура. Пусто до завершения генерации. */
  meanings?: WordMeaning[];
  /** Смыслы, отмеченные для тренировки: каждый — отдельный показ слова в сессии.
   *  Ничего не отмечено — бэк отдаёт основной смысл. Пусто у pending-карточек. */
  sessionSenseIds?: string[];
}

export interface DashboardStats {
  streak: number;
  wordsInMemory: number;
  weekDelta: number;
  accuracy: number;
  todayDone: number;
  todayGoal: number;
  /** 7 дней МСК, от шести дней назад до сегодня: была ли законченная тренировка (виджет «Сегодня»). */
  weekDays: boolean[];
  /** Модуль короткой тренировки на сегодня — тот же, куда зовёт письмо; null — тренировать нечего. */
  todayTarget: TodayTarget | null;
  /** Сегодня (день МСК) уже есть законченная тренировка — день засчитан в серию. */
  dayCounted: boolean;
  /** Что повторить сегодня и завтра по всем модулям; null — старый бэкенд. */
  plan: DayPlan | null;
  /** Когда серия даст несгораемые жетоны (2026-10-22); null — начисления выключены или старый бэкенд. */
  streakReward: StreakReward | null;
}

/** Награда за серию: через сколько дней и сколько жетонов. 0 — сегодняшняя тренировка закроет серию. */
export interface StreakReward {
  inDays: number;
  tokens: number;
}

/** Сводка дня по истории тренировок (не по FSRS, 2026-10-21); слово из двух модулей — один раз. */
export interface DayPlan {
  /** Слов в тренировках всех доступных модулей. */
  ready: number;
  /** Повторить сегодня: с ошибкой в прошлый раз и не встречавшиеся 3+ дня. */
  repeat: number;
  mistakes: number;
  stale: number;
  /** Ещё ни разу не отвечали в тренировках. */
  new: number;
  doneToday: number;
  tomorrow: number;
  /** До двух слов с ошибкой, которые завтра пойдут первыми. */
  tomorrowFirst: string[];
  modulesToRepeat: number;
  modulesWithWords: number;
}

export interface TodayTarget {
  moduleId: string;
  title: string;
  /** Готовых карточек в модуле: в тренировку войдёт не больше 10. */
  readyCount: number;
}

/** Лёгкая сводка активной (незавершённой) сессии для дашборда. Из неё рисуется
 *  плашка «Продолжить»: размер очереди зафиксирован при старте, прогресс/ошибки
 *  восстановлены из per-item статусов на бэке. done = total − remaining. */
export interface ActiveSessionSummary {
  id: string;
  moduleId: string;
  status: 'in_progress' | 'is_ready_to_boss';
  total: number;
  remaining: number;
  done: number;
  correct: number;
  wrong: number;
  seen: number;
  /** Карточки в статусе to_repeat — промахнулся и ещё НЕ пересдал (= красные
   *  точки в полосе прогресса). Это «осталось доработать», в отличие от `wrong`
   *  (суммарное число промахов за сессию, включая уже пересданные). */
  toRedo: number;
  /** Режим самостоятельной практики (если это cram-сессия), иначе null. */
  cramMode: string | null;
}
